<template>
    <div class="field--phone" @click="setFocusOnPhoneField">
        <div
            class="field--phone__label"
            :style="{ opacity: isLabelShown ? 1 : 0 }"
        >
            <span>{{ t(`${TRANSLATION_KEY}.phoneNumber`) }}</span>
        </div>

        <div class="row prefix-row">
            <PhonePrefixSelect
                v-model="formData.phonePrefix"
                class="prefix custom-select"
                :only-countries="listOfCountriesNew"
                :default-country="bindProps.defaultCountry"
                :disabled="disabled"
                @update:model-value="emitValue"
            />
        </div>

        <Error
            v-if="handlesError"
            :name="name + 'Prefix'"
            :value-field="formData.phonePrefix"
        />

        <div class="row">
            <Field
                v-model="formData.phone"
                :name="name"
                type="text"
                :disabled="disabled"
                :placeholder="placeholder"
                :is-validated="false"
                :handles-data-preload="handlesDataPreload"
                :is-numeric="true"
                :only-numbers="true"
                @blur="validate"
                @input="emitValue"
                @change="emitValue"
                @paste="emitValue"
            />
        </div>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    reactive,
    computed,
    onMounted,
    watch,
} from 'vue';
import { useStore } from 'vuex';
import Field from '@/components/basic/fields/Field.vue';
import Error from '@/components/basic/Error.vue';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import { useI18n } from 'vue-i18n';
import PhonePrefixSelect from './PhonePrefixSelect.vue';
import type { PhoneInterface } from '@/types';
import TheCountrySelect from '../basic/fields/TheCountrySelect.vue';

export default defineComponent({
    name: 'PhoneField',
    components: {
        Field,
        Error,
        PhonePrefixSelect,
        TheCountrySelect,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        modelValue: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        isValidated: {
            type: Boolean,
            default: true,
        },
        handlesDataPreload: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['blur', 'update:modelValue'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const TRANSLATION_KEY = 'compositeFields.PhoneField';
        const store = useStore();

        const formData = reactive<PhoneInterface>({
            phone: props.modelValue.phone || '',
            phonePrefix: props.modelValue.phonePrefix || '',
        });

        const prefixHasError = ref(false);
        const listOfCountriesNew = ref<string[]>([]);
        const preferredCountries = ref<string[]>([]);
        const isInputFocused = ref(false);

        const bindProps = reactive({
            defaultCountry:
                store.state.analyticsService?.analyticsData?.residency || '',
        });

        const phonePrefixesList = computed(() => {
            return store.state.config?.phonePrefixesList || [];
        });

        const isErrorsChanged = computed(() => store.state.isErrorsChanged);
        const email = computed(() => {
            const resp = store.state.response;
            if (!resp) return null;
            return resp.email || resp.info?.Email || null;
        });

        onMounted(() => {
            document.addEventListener('click', blurFocusOnDateField);
        });

        watch(
            phonePrefixesList,
            (newVal) => {
                if (newVal && newVal.length > 0) {
                    listOfCountriesNew.value = newVal;
                    if (UtilityHelper.getLocale() === 'ru') {
                        preferredCountries.value = [
                            'UA',
                            'PL',
                            'RU',
                            'LV',
                            'LT',
                            'BY',
                        ];
                        listOfCountriesNew.value =
                            listOfCountriesNew.value.filter(
                                (el) => !preferredCountries.value.includes(el)
                            );
                        listOfCountriesNew.value.sort();
                        listOfCountriesNew.value = [
                            ...preferredCountries.value,
                            ...listOfCountriesNew.value,
                        ];
                    }
                }
            },
            { immediate: true }
        );

        watch(
            email,
            (newEmail) => {
                if (!newEmail) return;
                const domain = newEmail.split('@')[1];
                const domainsWithUSA = [
                    'direktpoint.pl',
                    'wearehackerone.com',
                    'oanda.com',
                ];
                if (domainsWithUSA.includes(domain)) {
                    if (!listOfCountriesNew.value.includes('US')) {
                        listOfCountriesNew.value.push('US');
                    }
                }
            },
            { immediate: true }
        );

        function validate() {
            if (props.isValidated) {
                store.dispatch('validate', {
                    name: props.name,
                    value: formData.phone,
                });
            }
            emit('blur', formData);
        }

        function setFocusOnPhoneField() {
            if (!isInputFocused.value) {
                isInputFocused.value = true;
            }
        }

        function blurFocusOnDateField(evt: MouseEvent) {
            const target = evt.target as HTMLElement;
            if (!target.closest('.field--phone') && isInputFocused.value) {
                isInputFocused.value = false;
            }
        }

        function emitValue() {
            emit('update:modelValue', formData);
        }

        watch(isErrorsChanged, () => {
            prefixHasError.value = store.state.errors?.has(
                props.name + 'Prefix'
            );
        });

        return {
            t,
            TRANSLATION_KEY,
            formData,
            prefixHasError,
            listOfCountriesNew,
            preferredCountries,
            isInputFocused,
            bindProps,
            isLabelShown: computed(() =>
                Boolean(formData.phone.length || isInputFocused.value)
            ),
            validate,
            emitValue,
            setFocusOnPhoneField,
            blurFocusOnDateField,
        };
    },
});
</script>

<style scoped>
.field--phone__label {
    transition: opacity 0.2s;
}
</style>
